import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X } from 'lucide-react';

const EventsManager = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    location: '',
    type: 'upcoming',
    description: '',
    registration_open: true
  });
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/events`);
      const data = await response.json();
      if (data.success) setEvents(data.events);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const url = editingId
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/events/${editingId}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/events`;
      
      const payload = {
        ...formData,
        total_spots: formData.total_spots === '' ? null : parseInt(formData.total_spots, 10),
        fomo_spots_left: formData.fomo_spots_left === '' ? null : parseInt(formData.fomo_spots_left, 10)
      };
      const response = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        await fetchEvents();
        resetForm();
      }
    } catch (error) {
      console.error('Error saving event:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (event) => {
    setFormData({
      title: event.title,
      date: event.date,
      location: event.location,
      type: event.type,
      description: event.description,
      registration_open: event.registration_open,
      total_spots: event.total_spots ?? '',
      spots_mode: event.spots_mode || 'real',
      fomo_spots_left: event.fomo_spots_left ?? ''
    });
    setEditingId(event.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/events/${id}`, { method: 'DELETE' });
      await fetchEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  };

  const resetForm = () => {
    setFormData({ title: '', date: '', location: '', type: 'upcoming', description: '', registration_open: true, total_spots: '', spots_mode: 'real', fomo_spots_left: '' });
    setEditingId(null);
    setShowForm(false);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', fontWeight: '700' }}>Events Manager</h1>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {showForm ? <X size={20} /> : <Plus size={20} />}
          {showForm ? 'Cancel' : 'Add Event'}
        </button>
      </div>

      {showForm && (
        <div style={{ backgroundColor: '#FFFFFF', padding: '32px', borderRadius: '8px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)' }}>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '24px' }}>{editingId ? 'Edit Event' : 'New Event'}</h2>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gap: '20px' }}>
              <input type="text" placeholder="Event Title *" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required style={{ width: '100%', padding: '10px 14px', border: '1px solid #E0E0E0', borderRadius: '4px' }} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required style={{ padding: '10px 14px', border: '1px solid #E0E0E0', borderRadius: '4px' }} />
                <input type="text" placeholder="Location *" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} required style={{ padding: '10px 14px', border: '1px solid #E0E0E0', borderRadius: '4px' }} />
              </div>
              <textarea placeholder="Description *" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required rows="4" style={{ width: '100%', padding: '10px 14px', border: '1px solid #E0E0E0', borderRadius: '4px', fontFamily: 'var(--font-body)', resize: 'vertical' }} />
              <div style={{ display: 'flex', gap: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input type="checkbox" checked={formData.registration_open} onChange={(e) => setFormData({ ...formData, registration_open: e.target.checked })} />
                  Accepts registrations
                </label>
                <select value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })} style={{ padding: '8px 12px', border: '1px solid #E0E0E0', borderRadius: '4px' }}>
                  <option value="upcoming">Upcoming</option>
                  <option value="past">Past</option>
                </select>
              </div>

              {formData.registration_open && (
                <div style={{ padding: '16px', backgroundColor: '#FAF8F2', border: '1px solid #E7E0D4', borderRadius: '6px', display: 'grid', gap: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '6px' }}>Total spots available (leave blank for unlimited)</label>
                    <input type="number" min="0" placeholder="e.g. 100" value={formData.total_spots} onChange={(e) => setFormData({ ...formData, total_spots: e.target.value })} data-testid="event-total-spots" style={{ width: '100%', padding: '10px 14px', border: '1px solid #E0E0E0', borderRadius: '4px', boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '6px' }}>Spots left shown on the site</label>
                    <div style={{ display: 'flex', gap: '20px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input type="radio" name="spots_mode" checked={formData.spots_mode === 'real'} onChange={() => setFormData({ ...formData, spots_mode: 'real' })} data-testid="event-mode-real" />
                        Use real spots left
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input type="radio" name="spots_mode" checked={formData.spots_mode === 'fomo'} onChange={() => setFormData({ ...formData, spots_mode: 'fomo' })} data-testid="event-mode-fomo" />
                        FOMO spots left
                      </label>
                    </div>
                  </div>
                  {formData.spots_mode === 'fomo' && (
                    <div>
                      <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '6px' }}>FOMO spots left to display</label>
                      <input type="number" min="0" placeholder="e.g. 3" value={formData.fomo_spots_left} onChange={(e) => setFormData({ ...formData, fomo_spots_left: e.target.value })} data-testid="event-fomo-spots" style={{ width: '100%', padding: '10px 14px', border: '1px solid #E0E0E0', borderRadius: '4px', boxSizing: 'border-box' }} />
                      <p style={{ fontSize: '0.8rem', color: '#9A7B2E', marginTop: '6px' }}>This number is shown to visitors instead of the real count.</p>
                    </div>
                  )}
                </div>
              )}
              <div style={{ display: 'flex', gap: '12px' }}>
                <button type="submit" disabled={loading} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Save size={18} /> {loading ? 'Saving...' : 'Save Event'}
                </button>
                <button type="button" onClick={resetForm} style={{ padding: '12px 24px', border: '1px solid #E0E0E0', borderRadius: '4px', backgroundColor: 'transparent', cursor: 'pointer' }}>Cancel</button>
              </div>
            </div>
          </form>
        </div>
      )}

      <div style={{ backgroundColor: '#FFFFFF', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)', padding: '24px' }}>
        {loading && !showForm ? (
          <div style={{ textAlign: 'center', color: '#666666' }}>Loading...</div>
        ) : events.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#666666' }}>No events yet.</div>
        ) : (
          <div style={{ display: 'grid', gap: '16px' }}>
            {events.map((event) => (
              <div key={event.id} style={{ padding: '20px', border: '1px solid #E0E0E0', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h3 style={{ fontSize: '1.2rem', fontWeight: '600', marginBottom: '8px' }}>{event.title}</h3>
                  <p style={{ fontSize: '0.9rem', color: '#666666', marginBottom: '4px' }}>
                    {new Date(event.date).toLocaleDateString()} • {event.location}
                  </p>
                  <p style={{ fontSize: '0.85rem', color: '#999999' }}>{event.description.substring(0, 100)}...</p>
                  <span style={{ display: 'inline-block', marginTop: '8px', padding: '3px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600', backgroundColor: event.registration_open ? '#E8F5E9' : '#F1ECDD', color: event.registration_open ? '#2E7D32' : '#9A7B2E' }}>
                    {event.registration_open ? 'Accepts registrations' : 'By invitation only'}
                  </span>
                  {event.registration_open && (
                    <span data-testid={`event-count-${event.id}`} style={{ display: 'inline-block', marginTop: '8px', marginLeft: '8px', padding: '3px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600', backgroundColor: '#EEF3F5', color: '#3B6A7A' }}>
                      {event.registration_count || 0} registered{event.total_spots != null ? ` of ${event.total_spots}` : ''}
                      {event.spots_mode === 'fomo' ? ` (showing ${event.fomo_spots_left ?? 0})` : ''}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => handleEdit(event)} style={{ padding: '8px', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', color: '#111111' }}>
                    <Edit2 size={18} />
                  </button>
                  <button onClick={() => handleDelete(event.id)} style={{ padding: '8px', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', color: '#cc0000' }}>
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsManager;
